import { CommonTokenStream, InputStream, ParserRuleContext, Recognizer, Token } from 'antlr4';
import { ErrorListener } from 'antlr4/error/ErrorListener';
import { ParseTreeListener, ParseTreeWalker, TerminalNode } from 'antlr4/tree/Tree';
import { CustomAxisLexer } from 'generated/custom-axis/CustomAxisLexer';
import { CustomAxisParser } from 'generated/custom-axis/CustomAxisParser';

export type CustomExpressionAxis = 'x' | 'y';

export type ParsedExpression = {
  status?: 'valid' | 'error';
  error?: {
    message: string;
    type: 'invalid-expression' | 'no-attributes' | 'x-axis-only';
    details?: string;
  };
  formula: string;
  attributes: string[];
  /**
   * Numeric run attributes used as attr(${path}), referenced in the formula as _c0, _c1, ...
   * Set only for X-axis expressions.
   */
  runAttributes?: string[];
  /**
   * Defaults of runAttributes by index, from attr(${path}, default); null when there is no default.
   * Set only for X-axis expressions.
   */
  runAttributeDefaults?: (number | null)[];
};

type RunAttributeCall = { path: string; defaultLiteral: string };

class CustomAxisListener implements ParseTreeListener {
  constructor(
    private ruleNames: string[],
    private symbolicNames: string[],
  ) {}

  status?: 'valid' | 'error';
  formula = '';
  attributes: string[] = [];
  runAttributes: string[] = [];
  runAttributeDefaults: (number | null)[] = [];
  /** Default literal that is not a finite number, e.g. too large. */
  invalidRunAttributeDefault?: string;
  usesStep = false;
  private runAttributeCall?: RunAttributeCall;

  visitTerminal(node: TerminalNode): void {
    if (this.status) {
      return;
    }

    if (node.getSymbol().type === -1) {
      return; // EOF
    }

    const symbol = this.symbolicNames[node.getSymbol().type];
    const value = node.getText();

    if (this.runAttributeCall) {
      // attr(${path}) and attr(${path}, default) are emitted as a single _cN variable on exit.
      if (symbol === 'ATTRIBUTE_NAME') {
        this.runAttributeCall.path = value.trim();
      } else if (symbol === 'ADDITION_OPERATORS' || symbol === 'NUMBER_LITERAL') {
        this.runAttributeCall.defaultLiteral += value;
      }

      return;
    }

    if (['BEGIN_SUBSTITUTION', 'END_SUBSTITUTION'].includes(symbol)) {
      return;
    }

    if (symbol === 'STEP') {
      this.usesStep = true;
    }

    if (symbol === 'ATTRIBUTE_NAME') {
      const index = this.attributes.indexOf(value);

      if (index === -1) {
        this.formula += `_${this.attributes.length}`;
        this.attributes.push(value);
        return;
      }

      this.formula += `_${index}`;
      return;
    }

    this.formula += value;
  }

  visitErrorNode(): void {
    this.status = 'error';
  }

  enterEveryRule(node: ParserRuleContext): void {
    if (this.ruleNames[node.ruleIndex] === 'runAttributeFunction') {
      this.runAttributeCall = { path: '', defaultLiteral: '' };
    }
  }

  exitEveryRule(node: ParserRuleContext): void {
    if (this.ruleNames[node.ruleIndex] === 'runAttributeFunction') {
      const call = this.runAttributeCall;
      this.runAttributeCall = undefined;

      if (call && !this.status) {
        this.addRunAttribute(call);
      }
    }

    if (this.ruleNames[node.ruleIndex] === 'formula') {
      this.status = this.status ?? 'valid';
    }
  }

  // A variable is identified by the path and the default,
  // so attr(${a}) and attr(${a}, 0) are two variables.
  private addRunAttribute({ path, defaultLiteral }: RunAttributeCall): void {
    const defaultValue = defaultLiteral ? Number(defaultLiteral) : null;

    if (defaultValue !== null && !Number.isFinite(defaultValue)) {
      this.invalidRunAttributeDefault ??= defaultLiteral;
      return;
    }

    // -0 is the same default as 0.
    const normalizedDefault = defaultValue === 0 ? 0 : defaultValue;
    const index = this.runAttributes.findIndex(
      (runAttribute, i) =>
        runAttribute === path && this.runAttributeDefaults[i] === normalizedDefault,
    );

    if (index === -1) {
      this.formula += `_c${this.runAttributes.length}`;
      this.runAttributes.push(path);
      this.runAttributeDefaults.push(normalizedDefault);
      return;
    }

    this.formula += `_c${index}`;
  }
}

class SimpleErrorListener implements ErrorListener {
  error: string | undefined;

  syntaxError(
    recognizer: Recognizer,
    offendingSymbol: Token,
    line: number,
    column: number,
    msg: string,
    e: any,
  ) {
    void e;

    if (!this.error) {
      this.error = msg;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reportAmbiguity() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reportAttemptingFullContext() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reportContextSensitivity() {}
}

/**
 * Parses a custom expression. X-axis expressions (`axis: 'x'`) can also use `step` and
 * `attr(${path})`, which reads a numeric attribute of the run that owns the series.
 * `attr(${path}, default)` uses the numeric literal default when the run has no finite value.
 */
export const parseCustomAxis = (
  input: string,
  axis: CustomExpressionAxis = 'y',
): ParsedExpression => {
  const chars = new InputStream(input);
  const lexer = new CustomAxisLexer(chars);
  const tokens = new CommonTokenStream(lexer);
  const parser = new CustomAxisParser(tokens);

  const errorListener = new SimpleErrorListener();
  lexer.removeErrorListeners(); // Remove default console reporting.
  lexer.addErrorListener(errorListener);
  parser.removeErrorListeners(); // Remove default console reporting.
  parser.addErrorListener(errorListener);

  const tree = parser.formula();
  const listener = new CustomAxisListener(parser.ruleNames, parser.symbolicNames);
  ParseTreeWalker.DEFAULT.walk(listener, tree);

  if (errorListener.error !== undefined) {
    return {
      status: 'error',
      formula: '',
      attributes: [] as string[],
      error: {
        message: input.trim()
          ? 'Your expression seems to have an error'
          : 'The expression is empty',
        type: 'invalid-expression',
        details: errorListener.error,
      },
    };
  }

  if (listener.invalidRunAttributeDefault !== undefined) {
    return {
      status: 'error',
      formula: '',
      attributes: [] as string[],
      error: {
        message: 'The default value of attr(...) must be a finite number',
        type: 'invalid-expression',
        details: listener.invalidRunAttributeDefault,
      },
    };
  }

  const { formula, attributes, runAttributes, runAttributeDefaults, usesStep } = listener;

  if (axis === 'y') {
    const xAxisOnlySyntax = [
      ...(runAttributes.length > 0 ? ['attr(...)'] : []),
      ...(usesStep ? ['step'] : []),
    ];

    if (xAxisOnlySyntax.length > 0) {
      return {
        status: 'error',
        formula: '',
        attributes: [] as string[],
        error: {
          message: `${xAxisOnlySyntax.join(' and ')} can only be used in custom X-axis expressions`,
          type: 'x-axis-only',
        },
      };
    }
  }

  if (attributes.length === 0 && runAttributes.length === 0) {
    return {
      status: 'error',
      formula: '',
      attributes: [] as string[],
      error: {
        message: 'The expression must contain at least one attribute name',
        type: 'no-attributes',
      },
    };
  }

  const trimmedAttributes = attributes.map((attr) => attr.trim());

  if (axis === 'x') {
    return {
      status: 'valid',
      formula,
      attributes: trimmedAttributes,
      runAttributes,
      runAttributeDefaults,
    };
  }

  return { status: 'valid', formula, attributes: trimmedAttributes };
};

export const isCompletionFormula = (customAxis: ParsedExpression) =>
  customAxis.formula.includes('completion(_');

export function getChartExpressionStylingKey(expression: { alias?: string; expression: string }) {
  return `${expression.alias}:${expression.expression}`;
}
